/**
 * The Table Manager's host boundary (#1982).
 *
 * The shared composer owns neither data nor authority.  This file binds it to
 * the three governed doors that already own the contract: the resolver says
 * what the selected persona/host currently inherits, and the two authoring
 * verbs set or clear exactly that address.  Every coordinate is present in the
 * query key; changing a preview target can therefore never reuse another
 * target's document.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DEFAULT_TABLE_PRESENTATION,
  resolveTablePresentationEnvelope,
  type HostResolvedTablePresentation,
  type TablePresentation,
  type TablePresentationDocument,
} from '@solum/experience'
import { useSolum } from '../data/client'

export type TablePresentationHost = 'console' | 'portal'
export interface TablePresentationTarget {
  entity: string
  persona: string | null
  host: TablePresentationHost
}

export interface TablePresentationPersona { key: string; label: string }
export interface TablePresentationFormChoice {
  logical_key: string
  entity_type: string
  name: string
  scope: string
  persona_key: string | null
  sections: { title: string; fields?: string[] }[]
  is_default: boolean
  sort_order: number
}
export interface TablePresentationDefaultRow {
  key: string
  entity_type: string
  persona: string | null
  host: string
  document: TablePresentationDocument
  scope: string
  version: number
  updated_at: string
}

export const tablePresentationSettingsKey = (target: TablePresentationTarget) => [
  'table-presentation-settings', target.entity, target.persona, target.host,
] as const

export const tablePresentationTargetKey = (target: TablePresentationTarget) =>
  `${target.entity}:${target.persona == null ? 'layer=tenant' : `layer=persona:${target.persona}`}@${target.host}`

export const tablePresentationFormsKey = (target: TablePresentationTarget) => [
  'table-presentation-forms', target.entity, target.persona,
] as const

export const tableQuickCreateFormKey = (
  entity: string, persona: string | null, reference: string | undefined,
) => ['table-quick-create-form', entity, persona, reference] as const

export const tablePresentationOwnedKey = (target: TablePresentationTarget) =>
  `table.presentation.${target.entity}${target.persona ? `.${target.persona}` : ''}@${target.host}`

/** One terminal policy for both authoring verbs: refusal remains in place and
 * visible; only a confirmed server acceptance closes the in-place editor. */
export function tablePresentationWriteCallbacks(
  setFailure: (error: unknown) => void,
  onAccepted: () => void,
) {
  return {
    onSuccess: () => { setFailure(null); onAccepted() },
    onError: (error: unknown) => setFailure(error),
  }
}

/** Reset belongs only to the exact layer selected in the toolbar. */
export function targetOwnsTablePresentation(
  target: TablePresentationTarget,
  resolved: HostResolvedTablePresentation | undefined,
): boolean {
  if (!resolved || resolved.provenance.source !== 'declared') return false
  const scope = target.persona == null ? 'tenant' : 'persona'
  return resolved.provenance.scope === scope
    && (resolved.provenance.persona ?? null) === target.persona
    && (resolved.provenance.host ?? null) === target.host
    && resolved.provenance.experienceId === tablePresentationOwnedKey(target)
}

function authoringAnswer(
  wire: unknown,
  supported: readonly TablePresentation[],
): HostResolvedTablePresentation {
  const answer = resolveTablePresentationEnvelope(wire, supported)
  /* "No declaration" is a valid authoring start, not an invalid document the
     Save button must refuse.  Keep the provenance/fallback sentence, but hand
     the composer the shared neutral default and no synthetic validator issue. */
  if (answer.provenance.source === 'fallback') {
    return { ...answer, document: DEFAULT_TABLE_PRESENTATION, issues: [] }
  }
  return answer
}

function exactAuthoringAnswer(
  row: TablePresentationDefaultRow,
  supported: readonly TablePresentation[],
): HostResolvedTablePresentation {
  return authoringAnswer({
    entity: row.entity_type,
    persona: row.persona,
    host: row.host,
    document: row.document,
    available: row.document.availablePresentations,
    default: row.document.defaultPresentation,
    active: row.document.defaultPresentation,
    provenance: {
      source: 'declared', scope: row.scope, persona: row.persona, host: row.host,
      version: row.version, experience_id: row.key, active_source: 'default',
    },
    issues: [],
  }, supported)
}

export function useTablePresentationSettings(
  target: TablePresentationTarget | null,
  supported: readonly TablePresentation[],
) {
  const { client } = useSolum()
  return useQuery<HostResolvedTablePresentation>({
    queryKey: target ? tablePresentationSettingsKey(target) : ['table-presentation-settings', 'closed'],
    enabled: target != null,
    retry: false,
    queryFn: async () => {
      /* Both are needed. The inventory is the exact editable rung; the
         resolver is the inherited starting document when that rung is absent. */
      const [defaults, effective] = await Promise.all([
        client.raw().rpcTable<TablePresentationDefaultRow>('table_presentation_defaults', {}, 'core'),
        client.raw().rpcScalar('resolve_table_presentation', {
        p_entity: target!.entity,
        p_persona: target!.persona,
        p_host: target!.host,
        p_requested: null,
        }, 'core'),
      ])
      const exact = defaults.find((row) => row.entity_type === target!.entity
        && (row.persona ?? null) === target!.persona && row.host === target!.host
        && row.scope === (target!.persona == null ? 'tenant' : 'persona')
        && row.key === tablePresentationOwnedKey(target!))
      return exact ? exactAuthoringAnswer(exact, supported) : authoringAnswer(effective, supported)
    },
  })
}

/** The existing governed catalogues used by the authoring controls. */
export function useTablePresentationAuthority() {
  const { client } = useSolum()
  return useQuery<boolean>({
    queryKey: ['table-presentation-authority'], retry: false,
    queryFn: () => client.raw().rpcScalar<boolean>('table_presentation_authoring_allowed', {}, 'core'),
  })
}

/** The caller-visible, target-aware current deployment row behind a portable form reference. */
export function useResolvedQuickCreateForm(
  entity: string,
  persona: string | null,
  reference: string | undefined,
  enabled: boolean,
) {
  const { client } = useSolum()
  return useQuery<TablePresentationFormChoice | null>({
    queryKey: tableQuickCreateFormKey(entity, persona, reference),
    enabled,
    retry: false,
    queryFn: () => client.raw().rpcScalar<TablePresentationFormChoice | null>(
      'resolve_table_quick_create_form',
      { p_entity: entity, p_persona: persona, p_reference: reference ?? null },
      'core',
    ),
  })
}

export function useTablePresentationCatalogues(target: TablePresentationTarget | null, enabled: boolean) {
  const { client } = useSolum()
  const personas = useQuery<TablePresentationPersona[]>({
    queryKey: ['personas', 'list'],
    enabled,
    retry: false,
    queryFn: () => client.raw().rpcTable<TablePresentationPersona>('list_personas', {}, 'core'),
  })
  const forms = useQuery<TablePresentationFormChoice[]>({
    queryKey: target ? tablePresentationFormsKey(target) : ['table-presentation-forms', 'closed'],
    enabled: enabled && target != null,
    retry: false,
    queryFn: () => client.raw().rpcTable<TablePresentationFormChoice>('table_quick_create_forms', {
      p_entity: target!.entity, p_persona: target!.persona,
    }, 'core'),
  })
  return { personas, forms }
}

function invalidateTablePresentation(
  qc: ReturnType<typeof useQueryClient>,
  target: TablePresentationTarget,
) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['table-presentation-settings', target.entity] }),
    qc.invalidateQueries({ queryKey: ['experience-bundle', target.entity] }),
    qc.invalidateQueries({ queryKey: ['experience-resolution', target.entity, 'document:table'] }),
  ]).then(() => undefined)
}

export function useSetTablePresentation() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (value: TablePresentationTarget & { document: TablePresentationDocument }) =>
      client.raw().rpcScalar<string>('set_table_presentation', {
        p_entity: value.entity,
        p_persona: value.persona,
        p_host: value.host,
        p_document: value.document,
      }, 'core'),
    onSuccess: (_answer, value) => invalidateTablePresentation(qc, value),
  })
}

export function useClearTablePresentation() {
  const { client } = useSolum()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (value: TablePresentationTarget) =>
      client.raw().rpcScalar<boolean>('clear_table_presentation', {
        p_entity: value.entity,
        p_persona: value.persona,
        p_host: value.host,
      }, 'core'),
    onSuccess: (_answer, value) => invalidateTablePresentation(qc, value),
  })
}
